import React, { useCallback, useEffect, useState } from "react";
import { ChevronRight, Clock, ShieldAlert, Trophy } from "lucide-react";
import { useRouter } from "next/router";
import { HelpTooltip } from "@/components/atoms/HelpTooltip/HelpTooltip";
import { Button } from "@/components/ui/button";
import { CompetitionCard } from "@/components/ui/competition/CompetitionCard";
import { CompetitionListItem } from "@/components/ui/competition/CompetitionListItem";
import { CompetitionCardSkeleton, CompetitionListItemSkeleton } from "@/components/ui/competition/Skeletons";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getEligibleCompetitions } from "lib/actions";
import { CompetitionDTO } from "types";
interface EligibleCompetitionsModalProps {
  isOpen: boolean;
  onCancel: () => void;
  imageId: string | null;
}
const EligibleCompetitionsModal: React.FC<EligibleCompetitionsModalProps> = ({
  isOpen,
  onCancel,
  imageId
}) => {
  const router = useRouter();
  const [competitions, setCompetitions] = useState<CompetitionDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"cards" | "list">("list");
  useEffect(() => {
    if (isOpen && imageId) {
      setLoading(true);
      getEligibleCompetitions(imageId).then(response => {
        setCompetitions(response.competition || []);
        setLoading(false);
        setError(null);
      }).catch(() => {
        setError("Failed to fetch eligible competitions");
        setLoading(false);
      });
    }
  }, [isOpen, imageId]);
  const handleCompetitionClick = useCallback((compId: string) => {
    onCancel();
    router.push({
      pathname: `/competitions/${compId}`,
      query: {
        image: imageId
      }
    });
  }, [onCancel, imageId, router]);
  return <Dialog open={isOpen} onOpenChange={open => !open && onCancel()} data-sentry-element="Dialog" data-sentry-component="EligibleCompetitionsModal" data-sentry-source-file="index.tsx">
            <DialogContent className="sm:max-w-[500px] md:max-w-[600px] !max-h-[85vh] !p-0 bg-slate-900 border-slate-800 text-slate-200 overflow-hidden" data-sentry-element="DialogContent" data-sentry-source-file="index.tsx">
                <div className="flex flex-col h-full max-h-[85vh]">
                    {/* Header with gradient background */}
                    <DialogHeader className="!p-3 sm:!p-4 bg-gradient-to-r from-slate-900 to-slate-800 border-b border-slate-800" data-sentry-element="DialogHeader" data-sentry-source-file="index.tsx">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                                <Trophy className="h-5 w-5 text-emerald-400" data-sentry-element="Trophy" data-sentry-source-file="index.tsx" />
                                <DialogTitle className="!text-base sm:!text-lg !font-bold !text-white" data-sentry-element="DialogTitle" data-sentry-source-file="index.tsx">
                                    Eligible Competitions
                                </DialogTitle>
                            </div>
                            <HelpTooltip text="Submit your image to competitions it qualifies for. Competing can earn rewards and exposure for your work." data-sentry-element="HelpTooltip" data-sentry-source-file="index.tsx" />
                        </div>
                        <p className="!text-xs sm:!text-sm !text-slate-400 !mt-1">
                            Submit your creation to any of these competitions
                        </p>
                    </DialogHeader>

                    {/* View Toggle Buttons */}
                    <div className="flex justify-end p-2 bg-slate-950 border-b border-slate-800">
                        <div className="flex rounded-md overflow-hidden border border-slate-700">
                            <button onClick={() => setViewMode("list")} className={`px-2 py-1 text-xs font-medium flex items-center ${viewMode === "list" ? "bg-slate-700 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700/70"}`}>
                                List
                            </button>
                            <button onClick={() => setViewMode("cards")} className={`px-2 py-1 text-xs font-medium flex items-center ${viewMode === "cards" ? "bg-slate-700 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700/70"}`}>
                                Cards
                            </button>
                        </div>
                    </div>

                    {/* Content Area with overflow */}
                    <div className="flex-1 overflow-y-auto p-3 sm:p-4 bg-slate-950">
                        {/* Loading State */}
                        {loading && <div className="space-y-3">
                                {viewMode === "cards" ? <div className="grid grid-cols-1 gap-3">
                                        {[...Array(2)].map((_, i) => <CompetitionCardSkeleton key={i} />)}
                                    </div> : <>
                                        {[...Array(3)].map((_, i) => <CompetitionListItemSkeleton key={i} />)}
                                    </>}
                            </div>}

                        {/* Error State */}
                        {!loading && error && <div className="flex flex-col items-center justify-center py-6 text-center">
                                <ShieldAlert className="h-10 w-10 text-red-400 mb-3" />
                                <h3 className="!text-base !font-medium !text-red-400">
                                    Unable to load competitions
                                </h3>
                                <p className="!text-xs sm:!text-sm !text-slate-400 !mt-1 !max-w-xs">
                                    {error}. Please try again later.
                                </p>
                                <Button onClick={() => {
              setLoading(true);
              getEligibleCompetitions(imageId!).then(response => {
                setCompetitions(response.competition || []);
                setLoading(false);
                setError(null);
              }).catch(() => {
                setError("Failed to fetch eligible competitions");
                setLoading(false);
              });
            }} className="!mt-3 !px-3 !py-1.5 !bg-slate-800 hover:!bg-slate-700 !text-white !rounded-md !text-xs sm:!text-sm !font-medium" variant="secondary" size="sm">
                                    Try Again
                                </Button>
                            </div>}

                        {/* Empty State */}
                        {!loading && !error && competitions.length === 0 && <div className="flex flex-col items-center justify-center py-6 text-center">
                                <Trophy className="h-10 w-10 text-slate-600 mb-3" />
                                <h3 className="text-base font-medium text-slate-300">
                                    No eligible competitions found
                                </h3>
                                <p className="text-xs sm:text-sm text-slate-400 mt-1 max-w-xs">
                                    This image doesn&apos;t match the
                                    requirements for any currently active
                                    competitions.
                                </p>
                            </div>}

                        {/* Competition List */}
                        {!loading && !error && competitions.length > 0 && <div className="space-y-3">
                                {viewMode === "cards" ? <div className="grid grid-cols-1 gap-3">
                                        {competitions.map(competition => <CompetitionCard key={competition.id} competition={competition} onCompetitionClick={handleCompetitionClick} />)}
                                    </div> : <div className="space-y-2">
                                        {competitions.map(competition => <CompetitionListItem key={competition.id} competition={competition} onClick={() => handleCompetitionClick(competition.id)} />)}
                                    </div>}
                            </div>}
                    </div>

                    {/* Footer with action button */}
                    <div className="p-3 border-t border-slate-800 bg-slate-900 flex justify-between items-center">
                        <div className="text-xs text-slate-400 hidden sm:block">
                            <div className="flex items-center">
                                <Clock className="h-3 w-3 mr-1 text-emerald-400" data-sentry-element="Clock" data-sentry-source-file="index.tsx" />
                                <span>Competitions update regularly</span>
                            </div>
                        </div>
                        <Button onClick={onCancel} className="ml-auto !bg-slate-800 hover:!bg-slate-700 !text-white !text-xs sm:!text-sm !flex !items-center" variant="secondary" size="sm" data-sentry-element="Button" data-sentry-source-file="index.tsx">
                            Close
                            <ChevronRight className="h-3.5 w-3.5 ml-1" data-sentry-element="ChevronRight" data-sentry-source-file="index.tsx" />
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>;
};
export default EligibleCompetitionsModal;